import { type MaybeRefOrGetter, toRef, toValue } from 'vue'

import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import type { MessageInputMethod } from '~/api/shopping-assistant/types'

import type { EntryPoint } from './useShoppingAssistantContext'

const TRACKING_ZONE = 'ai_shopping_assistant'

export function useShoppingAssistantTracking(
  id: MaybeRefOrGetter<string | null>,
  entryPoint: MaybeRefOrGetter<EntryPoint>,
  entrySource?: MaybeRefOrGetter<string | null>,
) {
  const {
    trackFormSubmit,
    trackScreenView,
    trackClick,
    trackShoppingAssistantMessageSent,
    trackShoppingAssistantChatReady,
    trackShoppingAssistantProductClick,
    trackShoppingAssistantProductRecommendationReceived,
  } = useTracking()

  const chatId = toRef(() => toValue(id) || '')

  function getSource() {
    const source = toValue(entrySource ?? null)

    return source || null
  }

  function trackChatReady({ resumed }: { resumed: boolean }) {
    const entrySourceValue = getSource()

    trackShoppingAssistantChatReady({
      chatId: chatId.value,
      resumed,
    })
    trackScreenView({
      screenType: 'ai_chat_started',
      zone: TRACKING_ZONE,
      chatId: chatId.value,
      entryPoint: toValue(entryPoint),
      ...(entrySourceValue && { entrySource: entrySourceValue }),
      resumed,
    })
  }

  function trackMessageSent({
    inputMethod,
  }: {
    inputMethod: MessageInputMethod
  }) {
    const entrySourceValue = getSource()

    trackShoppingAssistantMessageSent({
      chatId: chatId.value,
      inputMethod,
    })
    trackFormSubmit({
      zone: TRACKING_ZONE,
      name: 'ai_chat_message_sent',
      chatId: chatId.value,
      inputMethod,
      entryPoint: toValue(entryPoint),
      ...(entrySourceValue && { entrySource: entrySourceValue }),
    })
  }

  function trackProductClicked({ product }: { product: Product }) {
    trackShoppingAssistantProductClick({
      chatId: chatId.value,
      product: {
        id: product.id,
        categoryId: product.categoryId,
        title: product.title,
        objectId: product.objectId,
      },
    })
  }

  function trackProductRecommendationReceived(products: Product[]) {
    if (products.length) {
      trackShoppingAssistantProductRecommendationReceived({
        products: products.map((product) => ({
          id: product.id,
          categoryId: product.categoryId,
          title: product.title,
          objectId: product.objectId,
        })),
      })
    }
  }

  function trackClicked(payload: Record<string, unknown>) {
    trackClick({
      ...payload,
      zone: TRACKING_ZONE,
      chatId: chatId.value,
    })
  }

  return {
    trackChatReady,
    trackMessageSent,
    trackProductClicked,
    trackClicked,
    trackProductRecommendationReceived,
  }
}
